import { UpChunk, createUpload } from '@mux/upchunk';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import auth from '@client/components/auth';
import _ from 'lodash';
import { UploadMap, UploadState } from './library.domain';
import { trackEvent } from '@services/tracking';
import { loadVideo } from '@client/new/helpers/load-video-from-file';
import { durationToLabel } from '../helpers';
import { getFileNameWihoutExtension } from '../helpers';

const safeTelemetry = (fn: () => void): void => {
  try {
    fn();
  } catch (telemetryError) {
    console.warn('Upload telemetry failed', telemetryError);
  }
};

type NavigatorWithConnection = Navigator & {
  connection?: { effectiveType?: string };
};

const getOnlineStatus = (): 'online' | 'offline' =>
  navigator.onLine ? 'online' : 'offline';

const getConnectionType = (): string => {
  const conn = (navigator as NavigatorWithConnection).connection;
  return conn?.effectiveType ?? 'unknown';
};

const MAX_FILENAME_LENGTH = 200;
const truncateFileName = (name: string): string =>
  name.length <= MAX_FILENAME_LENGTH
    ? name
    : name.slice(0, MAX_FILENAME_LENGTH);

export type ErrorClass = 'network' | 'auth' | 'server' | 'client' | 'unknown';
export type FileSizeBucket = '<100MB' | '100-500MB' | '500MB-2GB' | '>2GB';

const ONE_MB = 1024 * 1024;

export const getErrorClass = (httpStatus?: number): ErrorClass => {
  if (httpStatus === undefined) return 'network';
  if (httpStatus === 401 || httpStatus === 403) return 'auth';
  if (httpStatus >= 500 && httpStatus <= 599) return 'server';
  if (httpStatus >= 400 && httpStatus <= 499) return 'client';
  return 'unknown';
};

export const getFileSizeBucket = (bytes: number): FileSizeBucket => {
  if (bytes < 100 * ONE_MB) return '<100MB';
  if (bytes < 500 * ONE_MB) return '100-500MB';
  if (bytes < 2048 * ONE_MB) return '500MB-2GB';
  return '>2GB';
};

export const getTotalChunks = (
  fileSize: number,
  chunkSizeKB: number,
): number => {
  if (chunkSizeKB <= 0 || fileSize <= 0) return 0;
  return Math.ceil(fileSize / (chunkSizeKB * 1024));
};

export type MuxUploadErrorParams = {
  chunk: number;
  chunkSize: number;
  attempts: number;
  httpStatus?: number;
  underlyingMessage: string;
  videoId: string;
  influencerId: number;
  fileSize: number;
  fileName: string;
};

export class MuxUploadError extends Error {
  readonly chunk: number;
  readonly chunkSize: number;
  readonly attempts: number;
  readonly httpStatus?: number;
  readonly underlyingMessage: string;
  readonly videoId: string;
  readonly influencerId: number;
  readonly fileSize: number;
  readonly fileName: string;

  constructor(params: MuxUploadErrorParams) {
    super(
      `Mux upload failed at chunk ${params.chunk} after ${params.attempts} attempts`,
    );
    this.name = 'MuxUploadError';
    this.chunk = params.chunk;
    this.chunkSize = params.chunkSize;
    this.attempts = params.attempts;
    this.httpStatus = params.httpStatus;
    this.underlyingMessage = params.underlyingMessage;
    this.videoId = params.videoId;
    this.influencerId = params.influencerId;
    this.fileSize = params.fileSize;
    this.fileName = params.fileName;
  }
}

export type UseVideosParams = {
  influencerId: number;
};

export type UploadVideoParams = {
  video: UploadFile;
  influencerId: number;
  setUploadMap: React.Dispatch<React.SetStateAction<UploadMap>>;
  uploadMap: UploadMap;
  onSuccess?: (id: string) => void;
  folderId: number | null;
  tags?: string[];
};

export type MuxVideoParams = {
  chosenThumbnail?: string;
};

export type MuxUploadParams = {
  video: UploadFile;
  influencerId: number;
  editParams?: MuxVideoParams;
  onProgress: (progress: number) => void;
  onStarted: (videoId: string, startedAt: number) => void;
  onSuccess?: (id: string) => void;
  setMuxInstance: (instance: UpChunk) => void;
  folderId: number | null;
  tags?: string[];
};

export type UploadFile = {
  file: File;
  thumbnailTimestamp?: number;
  id: string;
};

export type BulkUploadParams = {
  uploadMap: UploadMap;
  setUploadMap: React.Dispatch<React.SetStateAction<UploadMap>>;
  influencerId: number;
  videos: UploadFile[];
  onSuccess?: () => void;
  folderId: number | null;
};

const chunkSize = 5120;

export const bulkUpload = async ({
  videos,
  influencerId,
  setUploadMap,
  uploadMap,
  onSuccess,
  folderId,
}: BulkUploadParams): Promise<void> => {
  const batches = _.chunk(videos, import.meta.env.VITE_MUX_CONCURRENT_UPLOADS);

  for (const videosBatch of batches) {
    const uploadPromises = videosBatch.map((v) =>
      uploadFile({
        video: v,
        influencerId,
        setUploadMap,
        uploadMap,
        onSuccess,
        folderId,
      }),
    );
    await Promise.all(uploadPromises);
  }
};

export const uploadFile = async ({
  video,
  setUploadMap,
  influencerId,
  onSuccess,
  folderId,
  tags,
}: UploadVideoParams) => {
  const updateState = (delta: Partial<UploadState>) => {
    setUploadMap((prev) => ({
      ...prev,
      [video.id]: { ...prev[video.id], ...delta },
    }));
  };

  updateState({
    fileId: video.id,
    fileName: video.file.name,
    progress: 0,
    state: 'uploading',
    folderId,
  });

  let startedAt: number | null = null;
  let resolvedVideoId: string | null = null;
  let lastProgress = 0;
  const totalChunks = getTotalChunks(video.file.size, chunkSize);
  const fileType = video.file.type || '';
  const fileSizeBucket = getFileSizeBucket(video.file.size);

  try {
    await muxUpload({
      video: video,
      influencerId: influencerId,
      folderId,
      tags,
      onStarted: (videoId, ts) => {
        resolvedVideoId = videoId;
        startedAt = ts;
      },
      onProgress: (value: number) => {
        lastProgress = value;
        updateState({
          progress: value,
          state: value === 100 ? 'finished' : 'uploading',
        });
      },
      onSuccess,
      editParams: {
        ...(video.thumbnailTimestamp
          ? { chosenThumbnail: String(video.thumbnailTimestamp) }
          : {}),
      },
      setMuxInstance: (value) =>
        setUploadMap((prev) => ({
          ...prev,
          [video.id]: { ...prev[video.id], muxInstance: value },
        })),
    });

    const successDurationMs = startedAt !== null ? Date.now() - startedAt : 0;
    const successVideoId = resolvedVideoId ?? '';

    loadVideo(video.file)
      .then((loaded) =>
        safeTelemetry(() =>
          trackEvent({
            type: 'Video Uploaded',
            payload: {
              'Files Uploaded': '1',
              'Upload Location': 'Library',
              'Video Type': durationToLabel(loaded.duration),
              'File Size Bucket': fileSizeBucket,
              'File Type': fileType,
              'Total Chunks': totalChunks,
              'Upload Duration MS': successDurationMs,
              'Video ID': successVideoId,
            },
          }),
        ),
      )
      .catch((loadError) =>
        console.warn('loadVideo failed after successful upload', loadError),
      );
  } catch (error) {
    console.error('Upload error', error);
    if (error instanceof MuxUploadError) {
      const errorDurationMs = startedAt !== null ? Date.now() - startedAt : 0;
      const detailsRaw = `${error.name}: ${error.underlyingMessage}`;
      safeTelemetry(() =>
        trackEvent({
          type: 'Video Upload Error',
          payload: {
            'File Type': fileType,
            Details: detailsRaw.slice(0, 500),
            'Video ID': error.videoId,
            'File Size Bucket': fileSizeBucket,
            'Failed Chunk': error.chunk,
            'Total Chunks': totalChunks,
            'Upload Location': 'Library',
            Attempts: error.attempts,
            'Error Class': getErrorClass(error.httpStatus),
            'HTTP Status': error.httpStatus ?? 0,
            'Error Message': error.underlyingMessage.slice(0, 200),
            'Upload Duration MS': errorDurationMs,
            'Progress Percent': Math.round(lastProgress),
            'Online Status': getOnlineStatus(),
            'Connection Type': getConnectionType(),
            'Chunk Size KB': chunkSize,
          },
        }),
      );
    }
    return updateState({
      state: 'error',
    });
  }
};

const muxUpload = async ({
  influencerId,
  onProgress,
  onStarted,
  video,
  editParams,
  onSuccess,
  setMuxInstance,
  folderId,
  tags,
}: MuxUploadParams) =>
  // eslint-disable-next-line no-async-promise-executor
  new Promise(async (res, rej) => {
    try {
      const data = await generateMuxSignedUrl({
        influencerId,
        fileSize: video.file.size,
        title: getFileNameWihoutExtension(video.file.name),
        folderId,
        tags,
        ...editParams,
      });

      if (!data.signedURL) {
        rej(
          new Error(
            'Mux signed URL response missing signedURL — cannot start upload',
          ),
        );
        return;
      }

      {
        const startedAt = Date.now();
        onStarted(data.videoId, startedAt);
        safeTelemetry(() =>
          trackEvent({
            type: 'Video File Upload Started',
            payload: {
              'Upload Location': 'Library',
              'Files Uploaded': 1,
              'File Type': video.file.type || '',
              'Total Chunks': getTotalChunks(video.file.size, chunkSize),
              'Chunk Size KB': chunkSize,
              'Video ID': data.videoId,
            },
          }),
        );

        safeTelemetry(() => {
          Sentry.addBreadcrumb({
            category: 'mux.upload',
            type: 'info',
            level: 'info',
            message: 'upload.start',
            data: {
              videoId: data.videoId,
              influencerId,
              fileSize: video.file.size,
              fileName: truncateFileName(video.file.name),
              fileType: video.file.type,
              chunkSizeKB: chunkSize,
            },
          });
        });

        // Status 0 = XHR with no response (network drop, TCP reset). UpChunk's
        // default retryCodes ([408, 502, 503, 504]) omits 0, so transient mid-PUT
        // failures were giving up after the first attempt. dynamicChunkSize lets
        // UpChunk shrink the chunk when a chunk takes >30s, reducing per-request
        // risk window on slow networks. With dynamicChunkSize on, the actual
        // number of chunks sent may differ from the at-start `Total Chunks`
        // estimate emitted in telemetry — that property is the starting
        // denominator, not a live count.
        //
        // attempts (default 5) + delayBeforeAttempt (default 1s) gave ~5s of
        // tolerance before giving up. Observed real failures: creators on 4g
        // hit transient drops, all 5 retries lined up inside the drop window,
        // and `Server responded with 0` was emitted while `navigator.onLine`
        // was still true (TCP reset, not a "real" offline event). UpChunk
        // natively pauses + resumes around real navigator offline/online
        // events without consuming attempts, so this tuning only needs to
        // cover pseudo-offline drops. attempts: 30 + delay: 5s gives ~150s
        // (~2.5 min) of tolerance, covering typical mobile network blips and
        // short tunnel/elevator outages.
        //
        // useLargeFileWorkaround swaps file.stream() (default) for FileReader
        // on chunk slices. Safari has known issues with file.stream() on
        // .mov/.mp4 > 2GB, and macOS iCloud Drive "Optimize Storage" can leave
        // a File handle whose stream() throws immediately — both surface as
        // `Unable to read file of size X bytes`. FileReader sidesteps both.
        const muxUploadInstance = await createUpload({
          file: video.file,
          endpoint: data.signedURL,
          chunkSize,
          retryCodes: [0, 408, 502, 503, 504],
          dynamicChunkSize: true,
          attempts: 30,
          delayBeforeAttempt: 5,
          useLargeFileWorkaround: true,
        });
        setMuxInstance(muxUploadInstance);

        muxUploadInstance.on('attempt', (e) => {
          safeTelemetry(() => {
            Sentry.addBreadcrumb({
              category: 'mux.upload',
              type: 'info',
              level: 'info',
              message: 'upload.attempt',
              data: {
                chunkNumber: e.detail?.chunkNumber,
                totalChunks: e.detail?.totalChunks,
                chunkSize: e.detail?.chunkSize,
              },
            });
          });
        });

        muxUploadInstance.on('chunkSuccess', (e) => {
          const chunkIndex = e.detail?.chunk;
          if (typeof chunkIndex !== 'number' || chunkIndex % 10 !== 0) return;
          safeTelemetry(() => {
            Sentry.addBreadcrumb({
              category: 'mux.upload',
              type: 'info',
              level: 'info',
              message: 'upload.chunk-success',
              data: {
                chunk: chunkIndex,
                chunkSize: e.detail?.chunkSize,
                attempts: e.detail?.attempts,
              },
            });
          });
        });

        muxUploadInstance.on('error', (e) => {
          const detail = e.detail ?? {};
          const httpStatus: number | undefined =
            detail.response?.statusCode ?? detail.response?.status;
          const uploadError = new MuxUploadError({
            chunk: typeof detail.chunk === 'number' ? detail.chunk : -1,
            chunkSize,
            attempts: typeof detail.attempts === 'number' ? detail.attempts : 0,
            httpStatus,
            underlyingMessage:
              typeof detail.message === 'string' ? detail.message : 'unknown',
            videoId: data.videoId,
            influencerId,
            fileSize: video.file.size,
            fileName: truncateFileName(video.file.name),
          });

          safeTelemetry(() => {
            Sentry.addBreadcrumb({
              category: 'mux.upload',
              type: 'error',
              level: 'error',
              message: 'upload.error',
              data: {
                chunk: uploadError.chunk,
                attempts: uploadError.attempts,
                httpStatus: uploadError.httpStatus,
                errorClass: getErrorClass(uploadError.httpStatus),
              },
            });
            Sentry.captureException(uploadError);
          });

          rej(uploadError);
        });
        muxUploadInstance.on('progress', (p) => onProgress(p.detail));
        muxUploadInstance.on('success', (p) => {
          onSuccess && onSuccess(data.videoId);
          res(p);
        });
      }
    } catch (e) {
      rej(e);
    }
  });

const generateMuxSignedUrl = ({
  influencerId,
  chosenThumbnail,
  fileSize,
  title,
  folderId,
  tags,
}: {
  influencerId: number;
  fileSize: number;
  title: string;
  chosenThumbnail?: string;
  folderId: number | null;
  tags?: string[];
}): Promise<{
  signedURL: string;
  videoId: string;
}> =>
  axios
    .post(
      `${import.meta.env.VITE_MUX_API_URL}/signed-url`,
      {
        influencerId,
        chosenThumbnail,
        fileSize,
        title,
        folderId,
        ...(tags?.length ? { tags } : {}),
      },
      {
        headers: {
          Authorization: auth.getToken(),
        },
      },
    )
    .then((response) => {
      if (response.status === 401) {
        throw new Error('auth');
      }
      return response.data;
    })
    .then((result) => {
      return {
        signedURL: result.signedURL,
        videoId: result.video.id,
      };
    });
